<template>
  <div>
    <div v-if="$userIsGraduate && placementStartComponentIsSignedOff" class="alert alert-info">
      <strong>Notice:</strong> This plan has been approved. Any changes will require resubmission for approval by your manager.
    </div><!-- /.alert -->

    <collapse-card name="directions">
      <template v-slot:title>
        <font-awesome :icon="['far', 'info-circle']" class="text-muted mr-2"/>Directions
      </template>

      <template v-if="$userIsGraduate">
        <p class="strong">Define 3-4 development objectives for this placement.</p>

        <p>These are activities you will complete as part of your development against the NSW Capability Framework.</p>

        <p class="strong">When creating your development objectives define which category is relevant:</p>
        <ul>
          <li>
            <strong>Experience:</strong> networking, rotations, stretch opportunities in your role
          </li>
          <li>
            <strong>Exposure:</strong> peers, mentors, executive sponsor, manager
          </li>
          <li>
            <strong>Education:</strong> Diploma of Government course, eBooks, journals, short courses, further education
          </li>
        </ul>

        <p class="strong">
          Development objectives should consider knowledge, skills, abilities or experiences to further develop capability level or meet performance objectives.
        </p>
      </template>
      <template v-else>
        <p>Development objectives enable a graduate to set clear goals for developing their capabilities throughout the 6-month placement. These objectives should consider knowledge, skills, abilities or experiences to further develop capability level or meet performance objectives.</p>

        <p>After reviewing your graduate’s performance and development objectives you will have an opportunity to advise the graduate if any changes are required, or you can approve and sign off the Start of Placement Plan.</p>
      </template>
    </collapse-card>

    <collapse-card name="example" v-if="$userIsGraduate">
      <template v-slot:title>Examples</template>

      <p>
        <strong>Development Objective</strong>
        <br>Improve teamwork, communication and collaboration skills.
      </p>

      <p class="mb-0">
        <strong>Action Steps</strong>
      </p>

      <ul>
        <li>Diploma of Government Workshop – Use Complex Workplace Communication Strategies.</li>
        <li>Attend team meetings.</li>
        <li>Seek feedback from peers and manager.</li>
      </ul>
    </collapse-card>

    <placements-start-component-capability-data-card v-if="$userIsGraduate"/>

    <h2 class="mt-5">Development Objectives</h2>

    <template v-if="$userIsGraduate">
      <placements-start-component-development-item
        v-for="(objective, index) in objectives"
        :key="index"
        :objective-index="index"
        :objective="objective"
      />
    </template>
    <template v-else-if="$userIsManager || $userIsCoordinator">
      <placements-development-item-read-only
        v-for="objective in objectives"
        :key="objective.id"
        :objective="objective"
      />
    </template>

    <template v-if="objectives.length < 4 && $userIsGraduate">
      <div class="decorative-divider"></div>

      <button type="button" class="btn btn-sm btn-outline-primary" @click="addBlankObjective">
        <font-awesome :icon="['far', 'plus']"/>Add Development Objective
      </button>
    </template>

    <div class="save-actions">
      <div
        class="alert alert-warning"
        v-if="objectives.length === 4 && $userIsGraduate"
      >You have reached the maximum of 4 development objectives.</div>

      <div class="alert alert-danger" v-if="otherErrors.length > 0">
        <font-awesome :icon="['fas', 'times']" class="float-right" @click="resetValidation"/>

        <p v-for="(error, index) in otherErrors" :key="`error-${index}`">{{ error }}</p>
      </div>

      <div v-if="$userIsGraduate" class="text-right">
        <button
          @click="discard"
          class="btn btn-link btn-discard mr-2"
        >Discard &amp; Exit</button>
        <ladda-button
          type="button"
          class="btn btn-outline-dark mr-2"
          :validate="false"
          :arguments="[{ draft: true, objectives: objectivesWithoutLastAction }]"
          :click="storeDevelopmentObjectives"
          :done="draftSaved"
          :failed="failed"
        >Save &amp; Exit</ladda-button>
        <ladda-button
          type="button"
          class="btn btn-success btn-submit-review"
          :validate="false"
          :click="confirmBeforeSubmittingForReview"
          :failed="failed"
          :done="reviewSubmitted"
        >Submit for Review</ladda-button>
      </div>
      <div v-else-if="$userIsManager || $userIsCoordinator" class="text-right">
        <router-link :to="{ name: 'graduates.placements.single', params: { placementId: placement.id, graduateId: placement.graduate_id } }" class="btn btn-link mr-2">
          Exit
        </router-link>
        <router-link :to="{ name: 'placements.single.start.manager.performance', params: { placementId: placement.id, graduateId: placement.graduate_id } }" class="btn btn-outline-dark mr-2">
          Back
        </router-link>
        <router-link :to="{ name: 'placements.single.start.manager.review', params: { placementId: placement.id, graduateId: placement.graduate_id } }" class="btn btn-success" active-class="">
          Next
        </router-link>
      </div>
    </div><!-- /.save-actions -->
  </div>
</template>

<style scoped>
svg.float-right {
  cursor: pointer;
}
</style>

<script>
import { map, cloneDeep } from 'lodash'
import { alert } from 'utils'
import { placementDevelopmentObjectiveComputed, placementDevelopmentObjectiveMethods, placementComputed, placementMethods } from 'vx/helpers'
import { populateObjectiveServerErrors } from './PlacementsStartComponentPerformance'
import { helpers } from 'mixins/placements'
import CollapseCard from 'components/CollapseCard'
import PlacementsStartComponentDevelopmentItem from './PlacementsStartComponentDevelopmentItem'
import PlacementsDevelopmentItemReadOnly from './PlacementsDevelopmentItemReadOnly'
import PlacementsStartComponentCapabilityDataCard from './PlacementsStartComponentCapabilityDataCard'

export default {
  mixins: [helpers],

  components: {
    CollapseCard,
    PlacementsStartComponentDevelopmentItem,
    PlacementsDevelopmentItemReadOnly,
    PlacementsStartComponentCapabilityDataCard
  },

  data () {
    return {
      otherErrors: [],
      errorsFromSubmit: [],
      doNotSubmit: false
    }
  },

  watch: {
    objectives: {
      handler () {
        if (this.objectives.length === 0) {
          this.addBlankObjective()
        }

        if (this.errors.any()) {
          this.$validator.reset()
        }
      },
      deep: true
    }
  },

  async created () {
    if (this.placementStartComponent.performance_objectives_completed === false) {
      this.$router.replace({ name: 'placements.single.start.performance' })

      return
    }

    await this.getDevelopmentObjectives()
  },

  computed: {
    ...placementComputed,
    ...placementDevelopmentObjectiveComputed,

    objectivesWithoutLastAction () {
      return map(cloneDeep(this.objectives), (objective) => {
        objective.actions.pop()

        return objective
      })
    }
  },

  methods: {
    ...placementMethods,
    ...placementDevelopmentObjectiveMethods,

    resetValidation () {
      this.otherErrors = []

      this.$validator.reset()
    },

    async discard () {
      if (
        !await alert.confirm({
          title: 'Discard Changes?',
          text: 'Are you sure you want to discard your changes.'
        })
      ) {
        return
      }

      this.$router.push({ name: 'placements.single' })
    },

    failed (error) {
      if (error.response && error.response.status === 422) {
        this.populateObjectiveServerErrors(error.response)
      }
    },

    populateObjectiveServerErrors,

    async confirmBeforeSubmittingForReview () {
      this.otherErrors = []

      if (
        !await alert.confirm({
          title: 'Submit for Review',
          text: 'Are you sure you want to submit your placement plan for review?'
        })
      ) {
        this.doNotSubmit = true
        return
      }
      this.doNotSubmit = false

      await this.storeDevelopmentObjectives({
        draft: false,
        objectives: this.objectivesWithoutLastAction
      })
    },

    async reviewSubmitted () {
      // If cancel button was pressed
      if (this.doNotSubmit) {
        return
      }

      // If we have just completed the objectives but the start component hasn't been updated
      // we'll fire off a request to refetch the placement.
      if (
        this.placementStartComponent.status === 'draft' ||
        this.placementStartComponent.development_objectives_completed === false
      ) {
        await this.getPlacement({ placement: this.placement.id, graduate: this.placement.graduate_id })
      }

      this.$router.push({ name: 'placements.single.start.review', params: { placementId: this.placement.id } })
    },

    draftSaved () {
      this.$noty.success('Development objectives have been saved successfully.')

      this.$router.push({ name: 'placements.single' })
    }
  }
}
</script>
